import { FunctionComponent } from 'react'
import styled, { css } from 'styled-components'
import Post from './Post'
import translate from '@i18n'
import darkSpinner from '@assets/images/spinner-dark.svg'
import { rgba } from 'polished'
import PrimaryButton from '@components/Buttons/Primary'
import { ScribbleWidgetProps } from './types'
import ScribbleContentContext from '@contexts/scribbleContent'
import useArticleScribblePolling from '@hooks/useArticleScribblePolling'

interface LoadingContainerProps {
  isFailed: boolean
}

const LoadingContainer = styled.div<LoadingContainerProps>`
  ${({
    theme: {
      colors: {
        grey: { grey_20 },
        brandColors: { brand_55 },
      },
    },
    isFailed,
  }) => css`
    background: ${grey_20};
    padding: 8px;
    display: flex;
    justify-content: center;
    align-items: center;

    ${isFailed &&
    css`
      border: ${brand_55} solid 1px;
      padding: 1em;
      background: ${rgba(brand_55, 0.1)};
    `}
  `}
`

const StyledScribbleWidget = styled.div`
  ${({
    theme: {
      colors: {
        grey: { grey_90 },
      },
    },
  }) => css`
    color: ${grey_90};

    figure {
      margin: 16px 0;
    }
  `}
`

const ButtonContainer = styled.div`
  padding: 40px 0 20px 0;
`

const LoadingSpinner = styled.span`
  @keyframes spin-animation {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  display: inline-block;
  animation-name: spin-animation;
  animation-iteration-count: infinite;
  animation-duration: 1s;
  animation-timing-function: linear;
  background: url(${darkSpinner}) no-repeat center;
  width: 20px;
  height: 20px;
  background-size: 20px 20px;
  margin-right: 8px;
`

const ScribbleWidget: FunctionComponent<ScribbleWidgetProps> = ({
  element: { id: scribbleId },
}) => {
  const {
    posts,
    fetchNextPage,
    hasNextPage,
    pageSize,
    isFailed,
    isLoading,
    postsLeft,
  } = useArticleScribblePolling({
    streamId: scribbleId,
  })
  if (isLoading || isFailed) {
    return (
      <LoadingContainer isFailed={!isLoading && isFailed}>
        {!isLoading ? (
          <div>
            <strong>{translate('scribble.failedBolded')}</strong>
            <span>{translate('scribble.failed')}</span>
          </div>
        ) : (
          <>
            <LoadingSpinner />
            <span>{translate('scribble.loading')}</span>
          </>
        )}
      </LoadingContainer>
    )
  }

  const numberOfItems = Math.min(pageSize, postsLeft)

  return (
    <StyledScribbleWidget>
      <ScribbleContentContext.Provider value={true}>
        {posts &&
          posts.map(({ id, dateInfo, icon, content, rank, meta }, i) => (
            <Post
              key={id}
              isLast={i === posts.length - 1}
              isPinned={rank === 0}
              metadataProps={{
                date:
                  typeof dateInfo === 'string' ? dateInfo : dateInfo.dateOrTime,
                icon,
              }}
              htmlViewProps={{
                content,
                meta,
              }}
            />
          ))}
        {hasNextPage && (
          <ButtonContainer>
            <PrimaryButton size="small" onClick={fetchNextPage}>
              {numberOfItems > 1
                ? translate('scribble.loadMore', {
                    numberOfItems: Math.min(pageSize, postsLeft).toString(),
                  })
                : translate('scribble.loadOneMore')}
            </PrimaryButton>
          </ButtonContainer>
        )}
      </ScribbleContentContext.Provider>
    </StyledScribbleWidget>
  )
}

export default ScribbleWidget
